import L from "../vendor/leaflet/leaflet.js"
import {closeRing, isDeleteKey, minVertices, openRing, removeVertexAt} from "./section_geometry.js"

const DESKTOP_MIN = 1024
const MIN_VERTICES = minVertices()

export const SectionMap = {
  mounted() {
    this.onResize = () => this.syncDesktopGate()
    this.onKeydown = (e) => this.handleDeleteKey(e)
    this.syncDesktopGate()
    window.addEventListener("resize", this.onResize)
    window.addEventListener("keydown", this.onKeydown)

    const slotCols = num(this.el.dataset.slotCols, 512)
    const slotRows = num(this.el.dataset.slotRows, 768)
    const tilePx = num(this.el.dataset.tilePx, 256)
    const slotNative = num(this.el.dataset.slotNativeZoom, 9)
    const mosaicUrl = this.el.dataset.mosaicUrl

    const slotW = slotCols * tilePx
    const slotH = slotRows * tilePx

    const map = L.map(this.el, {
      crs: L.CRS.Simple,
      zoomSnap: 0,
      zoomDelta: 0.25,
      minZoom: 0,
      maxZoom: slotNative,
      zoomControl: true,
      attributionControl: false,
      maxBoundsViscosity: 1,
      doubleClickZoom: false,
    })

    this.map = map
    this.slotCols = slotCols
    this.slotRows = slotRows
    this.tilePx = tilePx
    this.slotNative = slotNative
    this.layers = new Map()
    this.vertexGroup = L.layerGroup().addTo(map)
    this.midGroup = L.layerGroup().addTo(map)
    this.selectedUid = null
    this.hovered = null
    this.dragging = false

    const toLatLng = (x, y) => map.unproject([x, y], slotNative)
    this.toLatLng = toLatLng
    const worldBounds = L.latLngBounds(toLatLng(0, 0), toLatLng(slotW, slotH))
    map.setMaxBounds(worldBounds)

    if (mosaicUrl) {
      L.imageOverlay(mosaicUrl, worldBounds, {interactive: false, className: "carver-still"}).addTo(
        map
      )
    }

    map.fitBounds(worldBounds, {animate: false, padding: [12, 12]})

    this.handleEvent("sections_payload", ({sections, selected_uid}) => {
      this.selectedUid = selected_uid
      this.draw(sections || [])
    })
  },

  destroyed() {
    window.removeEventListener("resize", this.onResize)
    window.removeEventListener("keydown", this.onKeydown)
    if (this.map) this.map.remove()
  },

  syncDesktopGate() {
    const desktop = window.innerWidth >= DESKTOP_MIN
    const phone = document.getElementById("carver-desktop-only")
    const shell = document.getElementById("carver-desktop")
    if (phone) phone.hidden = desktop
    if (shell) shell.hidden = !desktop
    if (this.el) this.el.hidden = !desktop
  },

  draw(sections) {
    this.hovered = null
    for (const [, layer] of this.layers) this.map.removeLayer(layer)
    this.layers.clear()
    this.vertexGroup.clearLayers()
    this.midGroup.clearLayers()

    for (const section of sections) {
      const ring = ringOf(section.polygon)
      if (ring.length < MIN_VERTICES) continue
      const latlngs = openRing(ring).map(([u, v]) => this.uvToLatLng(u, v))
      const selected = section.uid === this.selectedUid
      const polygon = L.polygon(latlngs, {
        color: section.color || "#c45c26",
        weight: selected ? 3 : 1.5,
        fillOpacity: selected ? 0.28 : 0.16,
        interactive: true,
      }).addTo(this.map)

      polygon.on("click", (e) => {
        L.DomEvent.stop(e)
        this.pushEvent("select", {uid: section.uid})
      })

      this.layers.set(section.uid, polygon)
      if (selected) this.bindEditor(polygon, section.uid)
    }
  },

  bindEditor(polygon, uid) {
    this.vertexGroup.clearLayers()
    this.midGroup.clearLayers()
    this.bindVertices(polygon, uid)
    this.bindMidpoints(polygon, uid)
  },

  bindVertices(polygon, uid) {
    this.vertexGroup.clearLayers()
    const open = openLatLngs(polygon)

    open.forEach((ll, i) => {
      const marker = L.marker(ll, {
        draggable: true,
        zIndexOffset: 700,
        title: "Drag to move · double-click, right-click, or Delete to remove",
        // Leaflet positions the icon element with an inline transform, so the
        // visuals live in an inner span where CSS transforms still apply.
        icon: L.divIcon({
          className: "carver-vertex",
          html: '<span class="carver-vertex-core"></span>',
          iconSize: [14, 14],
        }),
      }).addTo(this.vertexGroup)

      marker.on("click", (e) => L.DomEvent.stop(e))
      marker.on("mouseover", () => {
        this.hovered = {uid, index: i}
        const el = marker.getElement()
        if (el) el.classList.add("carver-vertex--hot")
      })
      marker.on("mouseout", () => {
        if (this.hovered && this.hovered.uid === uid && this.hovered.index === i) {
          this.hovered = null
        }
        const el = marker.getElement()
        if (el) el.classList.remove("carver-vertex--hot")
      })
      marker.on("dblclick", (e) => {
        L.DomEvent.stop(e)
        this.deleteVertex(polygon, uid, i)
      })
      marker.on("contextmenu", (e) => {
        L.DomEvent.stop(e)
        this.deleteVertex(polygon, uid, i)
      })
      marker.on("dragstart", () => {
        this.dragging = true
      })
      marker.on("drag", () => {
        const pts = openLatLngs(polygon)
        pts[i] = marker.getLatLng()
        polygon.setLatLngs([pts])
        this.bindMidpoints(polygon, uid)
      })
      marker.on("dragend", () => {
        this.dragging = false
        this.commit(polygon, uid)
      })
    })
  },

  bindMidpoints(polygon, uid) {
    this.midGroup.clearLayers()
    const open = openLatLngs(polygon)
    const n = open.length
    if (n < 2) return

    for (let i = 0; i < n; i++) {
      const a = open[i]
      const b = open[(i + 1) % n]
      const mid = L.latLng((a.lat + b.lat) / 2, (a.lng + b.lng) / 2)
      const insertAt = i + 1
      const marker = L.marker(mid, {
        draggable: false,
        zIndexOffset: 650,
        title: "Click to add a vertex on this edge",
        icon: L.divIcon({
          className: "carver-midpoint",
          html: '<span class="carver-midpoint-core"></span>',
          iconSize: [10, 10],
        }),
      }).addTo(this.midGroup)

      marker.on("click", (e) => {
        L.DomEvent.stop(e)
        this.insertVertex(polygon, uid, insertAt, marker.getLatLng())
      })
    }
  },

  insertVertex(polygon, uid, index, latlng) {
    const open = openLatLngs(polygon)
    open.splice(index, 0, latlng)
    polygon.setLatLngs([open])
    this.bindEditor(polygon, uid)
    this.commit(polygon, uid)
  },

  deleteVertex(polygon, uid, index) {
    this.hovered = null
    const open = openLatLngs(polygon)
    const next = removeVertexAt(open, index)
    if (next.length === open.length) return
    polygon.setLatLngs([next])
    this.bindEditor(polygon, uid)
    this.commit(polygon, uid)
  },

  commit(polygon, uid) {
    const coords = openLatLngs(polygon).map((ll) => this.latlngToUv(ll))
    this.pushPolygon(uid, closeRing(coords))
  },

  pushPolygon(uid, coordinates) {
    this.pushEvent("polygon_changed", {uid, coordinates})
  },

  handleDeleteKey(e) {
    if (!isDeleteKey(e.key)) return
    const target = e.target
    if (
      target &&
      (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))
    ) {
      return
    }
    if (!this.hovered || this.dragging) return
    e.preventDefault()
    const {uid, index} = this.hovered
    this.hovered = null
    const polygon = this.layers.get(uid)
    if (!polygon) return
    this.deleteVertex(polygon, uid, index)
  },

  uvToLatLng(u, v) {
    return this.toLatLng(u * this.slotCols * this.tilePx, v * this.slotRows * this.tilePx)
  },

  latlngToUv(latlng) {
    const pt = this.map.project(latlng, this.slotNative)
    return [pt.x / (this.slotCols * this.tilePx), pt.y / (this.slotRows * this.tilePx)]
  },
}

function openLatLngs(polygon) {
  const ring = (polygon.getLatLngs()[0] || []).slice()
  if (ring.length >= 2 && ring[0].equals(ring[ring.length - 1])) return ring.slice(0, -1)
  return ring
}

function ringOf(polygon) {
  const coords = polygon && polygon.coordinates
  if (!Array.isArray(coords) || !Array.isArray(coords[0])) return []
  return coords[0]
}

function num(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}
